import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';

import { FormBuilder, Validators, ReactiveFormsModule, FormControl, FormControlStatus, FormGroup } from '@angular/forms';
import isEqual from 'lodash-es/isEqual';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-card';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-notification';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MasterDataService } from '../../../services/master-data.service';
import { ToastrService } from '../../../services/toastr-service'; // Ensure you have ToastrService injected
import { catchError, combineLatest, debounceTime, distinctUntilChanged, forkJoin, ReplaySubject, take, takeUntil } from 'rxjs';
import { equipmentCometValidator } from './validators/equipment-incomet.validator';
import { equipmentMaximumRepairCostValidator } from './validators/equipment-maximum-repair-cost.validator';
import { Shop } from '../../../models/shop.model';
import { Mode, DamageType } from '../../../models/mode.model';
import { Equipment } from '../../../models/equipment.model';
import { locationMismatchValidatorWarning } from './validators/location-mismatch-warning.validator';
import { HeaderForm } from '../../../form-model/cwo-header.form.model';
import { equipmentDetailsValidator } from './validators/equipment-details.validator';
import { ErrorComponent } from '../../../shared-components/error/error.component';
import { Warning } from '../../../models/warning';
import { DamageCause } from '../../../models/damage-cause.model';
import { WorkOrderService } from '../../../services/work-order.service';
import { User } from '../../../models/user.model';
import { PrincipalProperty } from '../../../models/principal-property.model';
import { ModeFilterService } from './mode-filter.service';
import { SharedDataService } from '../../../services/shared-data.service';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { SalesRestrictionOverrideModalComponent } from './sales-restriction-override-modal.component';
@Component({
  selector: 'app-cwo-header',
  imports: [ReactiveFormsModule, NgxSkeletonLoaderModule, ErrorComponent, SalesRestrictionOverrideModalComponent],
  templateUrl: './cwo-header.component.html',
  styleUrl: './cwo-header.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [ModeFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateWorkOrderHeaderComponent implements OnInit, OnDestroy {
  @Input({ required: true }) headerForm!: FormGroup<HeaderForm>;
  @Output() headerFormValid = new EventEmitter<FormControlStatus>();
  @Output() modeChanged = new EventEmitter<Mode>();
  @Output() shopChanged = new EventEmitter<Shop>();
  @Output() equipmentChanged = new EventEmitter<Equipment>();
  @Output() principalPropertyChanged = new EventEmitter<PrincipalProperty>();
  @Input() warnings = signal<Warning[]>([]);
  /** The exception reason already persisted in the DB for the current work order (edit mode). */
  @Input() storedExceptionReason: string | null = null;
  /** Emits when the sales restriction override reason changes (non-null = active override, null = cleared) */
  @Output() salesRestrictionOverrideChanged = new EventEmitter<string | null>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  masterdataLoading = true;
  equipmentDetailLoading = signal<boolean>(false);
  shops: Shop[] = [];
  causes: DamageCause[] = [];
  allModes: Mode[] = [];
  filteredModes: Mode[] = [];
  isThirdPartyDamage = signal<boolean>(false);
  errorControls = signal<string[]>([]);
  showOverrideModal = false;

  /** Expose the ModeFilterService signals for use in the template */
  get salesDemandState() {
    return this.modeFilter.salesDemandState;
  }
  get salesDemandOverride() {
    return this.modeFilter.salesDemandOverride;
  }
  constructor(
    private masterDataService: MasterDataService,
    private workOrderService: WorkOrderService,
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private modeFilter: ModeFilterService,
    private sharedDataService: SharedDataService,
    private appTextSvc: ApplicationtTextService,
  ) {}
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onRequestExceptionClicked(): void {
    this.showOverrideModal = true;
  }

  onOverrideConfirmed(reason: string): void {
    this.showOverrideModal = false;
    const equipment = this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.value!;
    this.updateFilteredModes(equipment, reason);
  }

  onOverrideCancelled(): void {
    this.showOverrideModal = false;
  }

  onOverrideModalClosed(): void {
    this.showOverrideModal = false;
  }

  ngOnInit(): void {
    this.addControlsToTheFormGroup();
    this.subscribeToShopModeEquipmentAndFormStatusChanges();
    this.getUserAssignedShop();
    this.subscribeToValueChanges<string>([
      {
        control: this.headerForm.controls.equipmentNumber,
        func: (controlValue) => {
          this.getEquipmentDetails(controlValue);
        },
      },
    ]);
    this.subscribeToValueChanges<DamageCause>([
      {
        control: this.headerForm.controls.cause,
        func: (value: DamageCause) => {
          if (value.code === 'CONFIRMED_3RD_PARTY') {
            this.isThirdPartyDamage.set(true);
            this.headerForm.controls.thirdPartyPort.addValidators([Validators.required, Validators.maxLength(10)]);
            this.headerForm.controls.thirdPartyPort.updateValueAndValidity();
          } else {
            this.isThirdPartyDamage.set(false);
            this.headerForm.controls.thirdPartyPort.clearValidators();
            this.headerForm.controls.thirdPartyPort.updateValueAndValidity();
          }
        },
      },
    ]);

    this.subscribeToValueChanges<Shop>([
      {
        control: this.headerForm.controls.shopCode,
        func: (value: Shop) => {
          this.headerForm.controls.currency.setValue(value?.maintenanceShopCurrency.currencyCode ?? '');
          this.shopChanged.emit(value);
          let equipmentNumber: string | undefined = this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.value?.equipmentNumber;
          if (equipmentNumber) {
            this.getEquipmentDetails(equipmentNumber);
          }
        },
      },
    ]);
    this.subscribeToValueChanges<Mode>([
      {
        control: this.headerForm.controls.mode,
        func: (value: Mode) => {
          this.modeChanged.emit(value);
          // Reset macOk and boxOk when mode changes and visibility conditions are no longer met
          if (!this.showMacOk) {
            this.headerForm.controls.macOk.setValue(false);
          }
          if (!this.showBoxOk) {
            this.headerForm.controls.boxOk.setValue(false);
          }
          let equipmentNumber: string | undefined = this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.value?.equipmentNumber;
          if (equipmentNumber && value) {
            this.validateEquipmentNumber();
          }
          // Get pti status if mode is 03 or starts with 4(reefer mode) and equipment number is present
          if ((value.code === '03' || value.code?.startsWith('4')) && equipmentNumber) {
            const equipment = this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.value;
            if (equipment) {
              this.masterDataService.getEquipmentPtiStatus(equipmentNumber, value.code).subscribe((pti) => {
                equipment.ptiStatus = pti;
                this.equipmentChanged.emit(equipment);
              });
            }
          }
        },
      },
    ]);
    this.headerForm.statusChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged()).subscribe((status) => {
      this.headerFormValid.emit(status);
    });

    this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe((status) => {
      this.headerForm.controls.equipmentNumber.setErrors(this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.errors);
    });
    this.subscribeToMasterDataApiCalls();
  }

  private getEquipmentDetails(equipmentNumber: string) {
    if (this.headerForm.controls.equipmentNumber.valid) {
      const shopLocationCode = this.headerForm.controls.shopCode.value?.locationCode;
      if (!shopLocationCode) {
        return;
      }

      this.equipmentDetailLoading.set(true);
      this.filteredModes = [];
      this.masterDataService
        .getEquipmentDetails(equipmentNumber, shopLocationCode)
        .pipe(
          catchError((err) => {
            this.equipmentDetailLoading.set(false);
            throw err;
          }),
        )
        .subscribe((equipment) => {
          if (!equipment) {
            this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.setValue(equipment);
            this.equipmentDetailLoading.set(false);
            return;
          }
          equipment.equipmentNumber = equipmentNumber;
          this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.setValue(equipment);
          this.equipmentChanged.emit(equipment);
          this.equipmentDetailLoading.set(false);
          this.updateFilteredModes(equipment);
          if (equipment.equipmentTypeCode === 'REEF') {
            this.masterDataService.getEquipmentAlarms(equipmentNumber).subscribe((x) => {
              equipment.alarms = x;
              this.equipmentChanged.emit(equipment);
            });
          }
          if (equipment.isLastMoveGateInEmpty) {
            this.validateEquipmentNumber();
          }
        });
    }
  }

  private updateFilteredModes(equipment: Equipment, salesOverrideReason?: string): void {
    this.modeFilter
      .filterModes(equipment, this.allModes, this.headerForm.controls.shopCode.value, salesOverrideReason)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((filteredModes) => {
        this.filteredModes = filteredModes;
        const newMode = this.filteredModes[0] ?? ({} as Mode);
        this.modeChanged.emit(newMode);
        this.headerForm.controls.mode.setValue(newMode);
        this.salesRestrictionOverrideChanged.emit(salesOverrideReason ?? null);
      });
  }

  private validateEquipmentNumber(): void {
    let equipmentNumber: string = this.headerForm.controls.equipmentNumber.value;
    let maintenanceRepairModeCode: string = this.headerForm.controls.mode.value.code;
    if (!equipmentNumber || !maintenanceRepairModeCode) {
      return;
    }

    this.workOrderService.validateEquipmentNumber(equipmentNumber, maintenanceRepairModeCode).subscribe((result) => {
      if (result.warnings.length > 0) {
        let filteredWarning = result.warnings.filter(
          (warning: { rulesetName: string; code: string }) => warning.rulesetName === 'equipment_past_repair_ruleset',
        );
        if (filteredWarning && filteredWarning.length > 0) {
          const pastRepairs = filteredWarning[0].metadata?.ExistingRepairCodes.split(',');
          this.sharedDataService.setPastRepairs(pastRepairs);
        }
      }
    });
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  get showMacOk(): boolean {
    return (
      this.headerForm.value.proxyEquipmentNumberForAsyncValidations?.equipmentTypeCode === 'REEF' &&
      this.headerForm.value.mode?.damageType === DamageType.STRUCTURAL
    );
  }
  get showBoxOk(): boolean {
    return (
      this.headerForm.value.proxyEquipmentNumberForAsyncValidations?.equipmentTypeCode === 'REEF' &&
      this.headerForm.value.mode?.damageType === DamageType.REEFER_UNIT
    );
  }
  private addControlsToTheFormGroup() {
    this.headerForm.addControl('shopCode', this.formBuilder.nonNullable.control<Shop>({} as Shop, Validators.required));
    this.headerForm.addControl(
      'equipmentNumber',
      this.formBuilder.nonNullable.control<string>('', {
        updateOn: 'blur',
        validators: [Validators.required, Validators.pattern(/^[A-Z]{4}[0-9]{6,9}$/)],
      }),
    );
    this.headerForm.addControl(
      'proxyEquipmentNumberForAsyncValidations',
      this.formBuilder.control<Equipment | null>(null, {
        validators: [equipmentCometValidator(), equipmentDetailsValidator()],
        asyncValidators: [equipmentMaximumRepairCostValidator(this.masterDataService, this.toastrService)],
      }),
    );
    this.headerForm.addControl('thirdPartyPort', this.formBuilder.nonNullable.control<string>(''));
    this.headerForm.addControl(
      'vendorRefNo',
      this.formBuilder.nonNullable.control<string>('', [Validators.maxLength(20), Validators.pattern(/^[^,&%]+$/)]),
    );
    this.headerForm.addControl('cause', this.formBuilder.nonNullable.control<DamageCause>({} as DamageCause, Validators.required));
    this.headerForm.addControl(
      'mode',
      this.formBuilder.nonNullable.control<Mode>({} as Mode, [
        Validators.required,
        (control) => (control.value?.code ? null : { required: true }),
      ]),
    );
    this.headerForm.addControl('currency', this.formBuilder.nonNullable.control<string>(''));
    this.headerForm.addControl('macOk', this.formBuilder.nonNullable.control<boolean>(false));
    this.headerForm.addControl('boxOk', this.formBuilder.nonNullable.control<boolean>(false));
    this.headerForm.addValidators([locationMismatchValidatorWarning(this.toastrService, this.appTextSvc)]);
  }
  private subscribeToValueChanges<T>(
    param: Array<{
      control: FormControl<T>;
      func: (value: T) => void;
    }>,
  ): void {
    param.forEach((element) => {
      element.control.valueChanges.pipe(distinctUntilChanged(), debounceTime(100), takeUntil(this.destroyed$)).subscribe(element.func);
    });
  }

  private setDefaultValueOfControls() {
    this.headerForm.controls.cause.setValue(this.causes[0]);
    if (this.shops.length > 0) {
      this.headerForm.controls.shopCode.setValue(this.shops[0]);
      this.headerForm.controls.currency.setValue(this.shops[0].maintenanceShopCurrency.currencyCode);
    }
  }

  private subscribeToMasterDataApiCalls() {
    forkJoin([this.masterDataService.getDamageCauses().pipe(take(1)), this.masterDataService.getRepairModes().pipe(take(1))]).subscribe(
      ([causes, modes]) => {
        this.causes = causes;
        this.allModes = modes;
        this.setDefaultValueOfControls();
        this.masterdataLoading = false;
      },
    );
  }

  private subscribeToShopModeEquipmentAndFormStatusChanges() {
    combineLatest([
      this.headerForm.controls.shopCode.valueChanges,
      this.headerForm.controls.mode.valueChanges,
      this.headerForm.controls.proxyEquipmentNumberForAsyncValidations.valueChanges,
      this.headerForm.statusChanges,
    ])
      .pipe(takeUntil(this.destroyed$), debounceTime(500), distinctUntilChanged(isEqual))
      .subscribe(([selectedShop, selectedMode, enteredEquipment, headerFormStatus]: [Shop, Mode, Equipment, FormControlStatus]) => {
        this.principalPropertyChanged.emit({
          Equipment: enteredEquipment,
          Shop: selectedShop,
          Mode: selectedMode,
          HeaderFormStatus: headerFormStatus,
        });
      });
  }

  private getUserAssignedShop() {
    const userData = localStorage.getItem('user');
    if (userData === null) {
      return;
    }
    const user: User = JSON.parse(userData);
    this.shops = user.maintenanceShops.filter((shop) => !shop.isVesselShop);
  }
}
